import React from 'react';
import {
  Dashboard as DashboardIcon,
  Class as ClassIcon,
  AssignmentTurnedIn as AttendanceIcon,
  Grading as MarksIcon,
  FactCheck as ExamsIcon,
  CalendarMonth as WeeklyIcon,
  Schedule as ScheduleIcon,
  EventNote as MeetingsIcon,
  ChildCare as DaycareIcon,
  NoteAlt as CareNotesIcon,
  Today as DailyUpdateIcon,
  QuestionAnswer as QueriesIcon,
  Receipt as ExpensesIcon,
  EventBusy as LeaveIcon,
  Event as HolidayIcon,
  Campaign as AnnouncementsIcon,
  BarChart as AnalyticsIcon,
} from '@mui/icons-material';
import HelpGuideLayout from '../../components/HelpGuideLayout';

const sections = [
  {
    id: 'dashboard',
    icon: DashboardIcon,
    title: 'Dashboard & Check-in',
    content: [
      {
        heading: 'Daily start',
        points: [
          'Check in when you arrive and check out when you leave — this records your attendance and work hours at the same time.',
          'Your dashboard shows your assigned classes, student/class counts, and upcoming parent meetings at a glance.',
        ],
      },
    ],
  },
  {
    id: 'classes',
    icon: ClassIcon,
    title: 'My Classes',
    content: [
      {
        heading: 'Managing your classes',
        points: [
          'Manage My Classes shows the classes and subjects assigned to you by the Principal — you can\'t create classes yourself.',
          'Use this to see your student rosters for each assigned class.',
        ],
      },
    ],
  },
  {
    id: 'attendance',
    icon: AttendanceIcon,
    title: 'Mark Attendance',
    content: [
      {
        heading: 'Daily attendance',
        points: [
          'Mark Attendance for each of your classes every day — this is always available, even if other academic features are switched off.',
          'Attendance status is case-sensitive in the system, so always use the provided present/absent toggle rather than typing it manually.',
        ],
      },
    ],
  },
  {
    id: 'marks',
    icon: MarksIcon,
    title: 'Exams & Marks',
    featureKey: 'academics',
    content: [
      {
        heading: 'Entering marks',
        points: [
          'Check Exam Schedule first — marks can only be entered against a specific exam created by your Principal.',
          'Enter Marks lets you record marks per student per subject for the selected exam and class; each student/subject/exam combination can only be entered once.',
        ],
      },
      {
        heading: 'Weekly Plans & My Schedule',
        points: [
          'Add Weekly Plan to submit your lesson plan for the upcoming week — your Principal can review these.',
          'My Schedule shows your full week\'s timetable, today\'s periods, and upcoming calendar events in one place.',
        ],
      },
    ],
  },
  {
    id: 'meetings',
    icon: MeetingsIcon,
    title: 'Parent Meetings',
    content: [
      {
        heading: 'Scheduling with parents',
        points: [
          'Manage Parent Meetings lets you schedule, reschedule or cancel meetings with a student\'s parent — parents are notified automatically.',
          'You must add notes when marking a meeting "Completed" — this is required and validated before it will save.',
        ],
      },
    ],
  },
  {
    id: 'daycare',
    icon: DaycareIcon,
    title: 'Daycare Roster',
    content: [
      {
        heading: 'Only if you\'re the assigned daycare teacher',
        points: [
          'Daycare Roster is only accessible if the Principal has assigned you as the school\'s daycare teacher.',
          'Check children in/out for daycare here; if you forget to check a child out, the system auto-closes the session overnight and flags it for you to fix the time the next day.',
        ],
      },
    ],
  },
  {
    id: 'care-notes',
    icon: CareNotesIcon,
    title: 'Care Notes',
    featureKey: 'care_notes',
    content: [
      {
        heading: 'Daycare-only communication',
        points: [
          'Care Notes only appear for students who are actively enrolled in daycare — they won\'t show for regular (non-daycare) students even if this feature is enabled school-wide.',
          'View today\'s notes from parents, Acknowledge that you\'ve read one, then mark it Completed once handled — completing a note emails and notifies the parent automatically.',
          'Completed notes can\'t be edited by you or the parent.',
        ],
      },
    ],
  },
  {
    id: 'daily-updates',
    icon: DailyUpdateIcon,
    title: 'Daily Updates',
    featureKey: 'daily_updates',
    content: [
      {
        heading: 'Posting a daily diary entry',
        points: [
          'Post Daily Update lets you log a student\'s meals, nap, mood and activities for the day — content is required, the rest are optional.',
          'Parents are notified in-app as soon as you post; you can edit or delete only your own entries.',
        ],
      },
    ],
  },
  {
    id: 'queries',
    icon: QueriesIcon,
    title: 'Parent Queries',
    featureKey: 'parent_queries',
    content: [
      {
        heading: 'Responding to parents',
        points: [
          'Parent Queries shows the queue of questions parents have raised for your school.',
          'Pick up a query to mark it "In Progress", then "Complete" it with a resolution note — this sends the parent an email, WhatsApp message and in-app notification.',
        ],
      },
    ],
  },
  {
    id: 'expenses',
    icon: ExpensesIcon,
    title: 'My Expenses',
    featureKey: 'expenses',
    content: [
      {
        heading: 'Claiming reimbursements',
        points: [
          'My Expenses lets you submit a reimbursement claim with amount, category, reason and date.',
          'You can edit or delete a claim only while it\'s still "Pending" — once your Principal approves, rejects or pays it, it\'s locked.',
        ],
      },
    ],
  },
  {
    id: 'leave',
    icon: LeaveIcon,
    title: 'Leave Management',
    featureKey: 'leave_management',
    content: [
      {
        heading: 'Applying for leave',
        points: [
          'Leave Management lets you submit a leave request; your balance is only deducted once the Principal approves it, not when you apply.',
        ],
      },
    ],
  },
  {
    id: 'holidays',
    icon: HolidayIcon,
    title: 'Holiday Calendar',
    content: [
      { heading: 'Viewing holidays', points: ['See all school holidays your Principal has published.'] },
    ],
  },
  {
    id: 'announcements',
    icon: AnnouncementsIcon,
    title: 'Announcements',
    featureKey: 'announcements',
    content: [
      { heading: 'Staying informed', points: ['View school-wide announcements posted by your Principal.'] },
    ],
  },
  {
    id: 'analytics',
    icon: AnalyticsIcon,
    title: 'Analytics',
    content: [
      { heading: 'Your class performance', points: ['View attendance and academic performance trends for your assigned classes.'] },
    ],
  },
];

const TeacherHelpGuide = () => (
  <HelpGuideLayout
    title="How to Use ClassTree — Teacher Guide"
    subtitle="A quick reference for everything you can do as a Teacher. Sections for features your school hasn't enabled are hidden automatically."
    sections={sections}
  />
);

export default TeacherHelpGuide;
